import { Button } from "@/components/ui";
import { useT } from "@/i18n";
import { cursorsMessages } from "@/i18n/messages/cursors";
import { Icons } from "@/components/ui/icons";
import { useWindowOrigin } from "@/desktop/use-window-origin";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { cn } from "@/lib/cn";
import { sendPresentationGuideCommand } from "@/lib/presentation-guide-control";
import type { AgentCursor } from "@/stores/canvas-store";
import { usePresentationGuideStore } from "@/stores/presentation-guide-store";
import "./presentation-guide-controls.css";

const ACTION_CUES = {
  click: { labelKey: "guideCueClick", Icon: Icons.MousePointerClick },
  type: { labelKey: "guideCueType", Icon: Icons.Keyboard },
  enter: { labelKey: "guideCueEnter", Icon: Icons.Keyboard },
  drag: { labelKey: "guideCueDrag", Icon: Icons.Hand },
} as const;

export function PresentationGuideControls({ cursor }: { cursor: AgentCursor }) {
  const t = useT(cursorsMessages);
  const guide = usePresentationGuideStore((state) => state.guide);
  const origin = useWindowOrigin();
  const displayId = getWindowDisplayId();
  const target = cursor.pointTarget;
  if (
    !guide ||
    guide.presentationId !== cursor.presentationDeliveryId ||
    !target ||
    !origin.ready ||
    (displayId !== null &&
      displayId !== (guide.anchor?.displayId ?? target.displayId))
  )
    return null;

  const x = target.globalX - origin.x;
  const y = target.globalY - origin.y;
  const annotation =
    cursor.annotationTarget?.seq === target.seq &&
    cursor.annotationTarget.annotationId ===
      `${guide.presentationId}:${guide.stepId}`
      ? cursor.annotationTarget
      : undefined;
  const pathStart =
    annotation &&
    (annotation.shape === "path" || annotation.shape === "polyline")
      ? annotation.path[0]
      : undefined;
  const cueX = annotation
    ? annotation.globalX -
      origin.x +
      annotation.width *
        (guide.action === "drag" && pathStart ? pathStart.x : 0.5)
    : x;
  const cueY = annotation
    ? annotation.globalY -
      origin.y +
      annotation.height *
        (guide.action === "drag" && pathStart ? pathStart.y : 0.5)
    : y;
  const controlX = guide.anchor ? guide.anchor.x - origin.x : x;
  const controlY = guide.anchor ? guide.anchor.y - origin.y : y;
  const paused = guide.phase === "paused";
  const waiting = guide.phase === "waiting";
  const cue = guide.action ? ACTION_CUES[guide.action] : undefined;
  const cueLabel = cue ? t(cue.labelKey) : undefined;
  const command = (
    action: "pause" | "resume" | "repeat" | "continue" | "cancel",
  ) => {
    void sendPresentationGuideCommand({
      presentationId: guide.presentationId,
      stepId: guide.stepId,
      action,
    });
  };

  return (
    <>
      {cue && !paused ? (
        <div
          key={`${guide.stepId}:${guide.replayCount}`}
          data-testid="presentation-guide-action-cue"
          data-action={guide.action}
          className={cn(
            "presentation-guide-cue pointer-events-none fixed",
            waiting && "presentation-guide-cue--waiting",
          )}
          style={{
            left: Math.max(8, Math.min(cueX + 16, window.innerWidth - 208)),
            top: Math.max(8, Math.min(cueY + 18, window.innerHeight - 48)),
          }}
          aria-hidden="true"
        >
          <span className="presentation-guide-cue-icon">
            <cue.Icon size={24} />
          </span>
          <span>{cueLabel}</span>
        </div>
      ) : null}
      <section
        aria-label={t("guideControlsLabel")}
        data-testid="presentation-guide-controls"
        data-presentation-guide-controls="true"
        data-interactive="true"
        className="presentation-guide-controls pointer-events-auto fixed"
        style={{
          left: controlX > window.innerWidth / 2 ? 24 : undefined,
          right: controlX > window.innerWidth / 2 ? "auto" : 24,
          top: controlY > window.innerHeight / 2 ? 24 : undefined,
          bottom: controlY > window.innerHeight / 2 ? "auto" : 24,
        }}
      >
        <div className="presentation-guide-heading">
          <span className="min-w-0">
            {t("guideStep", {
              current: guide.stepIndex + 1,
              total: guide.stepCount,
            })}
          </span>
          <span role="status" className="min-w-0 text-right">
            {paused
              ? t("guidePaused")
              : waiting
                ? t("guideYourTurn")
                : t("guideGuiding")}
          </span>
        </div>
        <p className="presentation-guide-copy">
          {paused
            ? t("guideHintPaused")
            : waiting
              ? t("guideHintWaiting", {
                  action: cueLabel ?? t("guideCueFallback"),
                })
              : t("guideHintGuiding")}
        </p>
        <div className="presentation-guide-actions">
          <Button
            variant="secondary"
            size="sm"
            data-testid="presentation-guide-pause-resume"
            onClick={() => command(paused ? "resume" : "pause")}
          >
            {paused ? <Icons.Play size={14} /> : <Icons.Pause size={14} />}
            {paused ? t("guideResume") : t("guidePause")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={paused}
            data-testid="presentation-guide-repeat"
            onClick={() => command("repeat")}
          >
            <Icons.RotateCcw size={14} /> {t("guideShowAgain")}
          </Button>
          {waiting ? (
            <Button
              size="sm"
              data-testid="presentation-guide-continue"
              onClick={() => command("continue")}
            >
              <Icons.Check size={14} /> {t("guideDoneContinue")}
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            data-testid="presentation-guide-cancel"
            className="!text-[rgb(213_238_221)]"
            onClick={() => command("cancel")}
          >
            {t("guideEnd")}
          </Button>
        </div>
      </section>
    </>
  );
}
